import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2, Trash2, Eye } from "lucide-react";
import { formatEURDecimal } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const eur = (v) => formatEURDecimal(v || 0);

export default function Archivos() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [yearFilter, setYearFilter] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const q = yearFilter ? `?year=${yearFilter}` : "";
      const r = await api.get(`/weekly-archives${q}`);
      setList(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando archivos");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [yearFilter]);

  const download = async (a) => {
    setDownloadingId(a.id);
    try {
      const r = await api.get(`/weekly-archives/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const el = document.createElement("a");
      el.href = url;
      el.download = a.filename || `archive_${a.id}.xlsx`;
      document.body.appendChild(el);
      el.click(); el.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error descargando"); }
    finally { setDownloadingId(null); }
  };

  const remove = async (a) => {
    if (!window.confirm(`¿Borrar el archivo "${a.filename}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/weekly-archives/${a.id}`);
      toast.success("Archivo borrado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error borrando"); }
  };

  const years = [...new Set(list.map(a => a.year))].sort((a,b)=>b-a);
  const totalSize = list.reduce((s, a) => s + (a.size_bytes || 0), 0);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="archivos-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">Histórico · Excel semanales</p>
          <h1 className="font-display text-4xl font-black tracking-tight">Archivos semanales</h1>
          <p className="text-sm text-[var(--ink-muted)] mt-1 max-w-2xl">
            Al abrir una nueva semana desde el Wizard, el sistema guarda automáticamente un Excel completo
            (proyectos × quincenas + BANCOS) del estado justo antes del cierre. Aquí puedes descargarlos.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-[var(--ink-muted)]">Año</label>
          <select
            data-testid="archivos-year-filter"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="border rounded px-3 py-2 text-sm"
          >
            <option value="">Todos</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Archivos" value={list.length}/>
        <Stat label="Años cubiertos" value={new Set(list.map(a=>a.year)).size}/>
        <Stat label="Peso total" value={`${(totalSize/1024/1024).toFixed(2)} MB`}/>
        <Stat label="Último archivo" value={list[0]?.created_at ? new Date(list[0].created_at).toLocaleDateString("es-ES") : "—"}/>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg)] text-xs uppercase text-[var(--ink-muted)] font-mono-num tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Archivo</th>
              <th className="text-left px-3 py-2">Año datos</th>
              <th className="text-left px-3 py-2">Cerrado en</th>
              <th className="text-left px-3 py-2">Creado por</th>
              <th className="text-right px-3 py-2">Bancos</th>
              <th className="text-right px-3 py-2">Saldo bancos</th>
              <th className="text-right px-3 py-2">Peso</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center py-8 text-[var(--ink-muted)]"><Loader2 className="inline animate-spin mr-2" size={13}/>Cargando…</td></tr>
            )}
            {!loading && list.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-[var(--ink-muted)]">
                Aún no hay archivos guardados. Se creará uno automáticamente la próxima vez que abras una nueva semana desde el Wizard.
              </td></tr>
            )}
            {!loading && list.map(a => (
              <tr key={a.id} className="border-t hover:bg-[var(--hover)]" data-testid={`archivo-row-${a.id}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet size={14} className="text-emerald-700 shrink-0"/>
                    <span className="font-medium truncate max-w-[380px]" title={a.filename}>{a.filename}</span>
                  </div>
                </td>
                <td className="px-3 py-2 font-mono-num">{a.year}</td>
                <td className="px-3 py-2 text-xs">
                  {new Date(a.created_at).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  {a.quincena_q && a.quincena_month ? (
                    <span className="ml-2 text-[var(--ink-muted)]">{a.quincena_q}ª Q · Mes {a.quincena_month}</span>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-xs text-[var(--ink-muted)] truncate max-w-[200px]" title={a.created_by}>{a.created_by || "—"}</td>
                <td className="px-3 py-2 text-right font-mono-num text-xs">{a.meta?.n_banks ?? "—"}</td>
                <td className="px-3 py-2 text-right font-mono-num text-xs">{a.meta?.total_banks_saldo != null ? eur(a.meta.total_banks_saldo) : "—"}</td>
                <td className="px-3 py-2 text-right font-mono-num text-xs text-[var(--ink-muted)]">{((a.size_bytes||0)/1024).toFixed(0)} KB</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Link
                    data-testid={`archivo-view-${a.id}`}
                    to={`/archivos/${a.id}`}
                    title="Ver cashflow completo de este archivo"
                    className="p-1 text-[var(--ink-muted)] hover:text-[var(--ink)] inline-flex"
                  >
                    <Eye size={13}/>
                  </Link>
                  <button
                    data-testid={`archivo-download-${a.id}`}
                    onClick={() => download(a)}
                    disabled={downloadingId === a.id}
                    title="Descargar Excel"
                    className="p-1 text-[var(--ink-muted)] hover:text-[var(--ink)] disabled:opacity-40"
                  >
                    {downloadingId === a.id ? <Loader2 size={13} className="animate-spin"/> : <Download size={13}/>}
                  </button>
                  {isAdmin && (
                    <button
                      data-testid={`archivo-delete-${a.id}`}
                      onClick={() => remove(a)}
                      title="Borrar (solo admin)"
                      className="p-1 text-[var(--ink-muted)] hover:text-rose-600"
                    >
                      <Trash2 size={13}/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-num">{label}</div>
      <div className="mt-1 font-mono-num text-2xl font-bold">{value}</div>
    </div>
  );
}
